import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  requestLoginOTP,
  verifyLoginOTP,
  resendSignupOTP as resendOTP,
} from "../../services/auth";
import { validateIndianPhone, formatIndianPhone } from "../../utils/validation";
import LoadingState from "../Common/LoadingState";
import AuthNavbar from "./AuthNavbar";
import "./Auth.css";
import { useTheme } from "../../contexts/ThemeContext";

declare global {
  interface Window {
    turnstile: any;
    onloadTurnstileCallback?: () => void;
  }
}

// Environment check
const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000XX";

enum LoginStep {
  PHONE_ENTRY,
  OTP_VERIFICATION,
}

// Back button component
const BackButton: React.FC = () => {
  return (
    <Link
      to="https://ukti.ai"
      className="flex items-center text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] transition-colors mb-4"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="mr-1"
      >
        <path d="M19 12H5M12 19l-7-7 7-7" />
      </svg>
      Back to Home
    </Link>
  );
};

interface LeadLoginProps {
  onLoginSuccess: () => void;
}

const LeadLogin: React.FC<LeadLoginProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const { theme } = useTheme(); // Get current theme from context

  // Form states
  const [phone, setPhone] = useState<string>("");
  const [otpCode, setOtpCode] = useState<string>("");
  const [step, setStep] = useState<LoginStep>(LoginStep.PHONE_ENTRY);

  // UI states
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [tryCount, setTryCount] = useState<number>(0);
  const [phoneValidationMsg, setPhoneValidationMsg] = useState<string | null>(
    null
  );
  const [showResendModal, setShowResendModal] = useState<boolean>(false);

  // Turnstile (captcha) references
  const turnstileRef = useRef<any>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileContainer = useRef<HTMLDivElement>(null);
  const turnstileScriptLoaded = useRef<boolean>(false);

  // Create separate turnstile references for the resend modal
  const modalTurnstileRef = useRef<any>(null);
  const modalTurnstileWidgetId = useRef<string | null>(null);
  const modalTurnstileContainer = useRef<HTMLDivElement>(null);

  // Load Turnstile script
  useEffect(() => {
    // Skip loading Turnstile in development
    if (IS_DEVELOPMENT || turnstileScriptLoaded.current) return;

    // Mark script as loaded to prevent duplicate loading
    turnstileScriptLoaded.current = true;

    // Create the script element
    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback";
    script.async = true;
    script.defer = true;
    script.id = "turnstile-script";

    // Set up the callback
    window.onloadTurnstileCallback = () => {
      initializeTurnstile();
    };

    // Add script to body
    document.body.appendChild(script);

    // Cleanup function
    return () => {
      if (document.getElementById("turnstile-script")) {
        document.body.removeChild(script);
      }
      // Clean up the callback when unmounting
      if (window.onloadTurnstileCallback) {
        delete window.onloadTurnstileCallback;
      }
      // Remove widget if it exists
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
    };
  }, []); // No dependencies to prevent reloading script

  // Add a function to initialize or reinitialize Turnstile
  const initializeTurnstile = () => {
    if (turnstileContainer.current && window.turnstile) {
      // If a widget already exists, remove it first
      if (turnstileWidgetId.current) {
        window.turnstile.remove(turnstileWidgetId.current);
      }

      // Create new widget
      turnstileWidgetId.current = window.turnstile.render(
        turnstileContainer.current,
        {
          sitekey: TURNSTILE_SITE_KEY,
          theme: theme === "light" ? "light" : "dark",
          callback: (token: string) => {
            turnstileRef.current = token;
          },
          "error-callback": () => {
            turnstileRef.current = null;
          },
        }
      );
    }
  };

  // Effect to handle theme changes
  useEffect(() => {
    if (!IS_DEVELOPMENT && turnstileScriptLoaded.current && window.turnstile) {
      initializeTurnstile();
    }
  }, [theme]);

  // Reset Turnstile when coming back to the phone entry step or when showResendCaptcha changes
  useEffect(() => {
    if (
      !IS_DEVELOPMENT &&
      turnstileWidgetId.current &&
      window.turnstile &&
      (step === LoginStep.PHONE_ENTRY || showResendModal)
    ) {
      window.turnstile.reset(turnstileWidgetId.current);
    }
  }, [step, showResendModal]);

  // Countdown timer effect for OTP resend
  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setTimeout(() => {
      setTimeLeft((time) => time - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  // Effect to initialize or reset Turnstile in the modal when it opens
  useEffect(() => {
    if (!IS_DEVELOPMENT && showResendModal && window.turnstile) {
      // Small delay to ensure the container is mounted in the DOM
      const timer = setTimeout(() => {
        if (modalTurnstileContainer.current) {
          // If a widget already exists, remove it first
          if (modalTurnstileWidgetId.current) {
            window.turnstile.remove(modalTurnstileWidgetId.current);
          }

          // Create new widget
          modalTurnstileWidgetId.current = window.turnstile.render(
            modalTurnstileContainer.current,
            {
              sitekey: TURNSTILE_SITE_KEY,
              theme: theme === "light" ? "light" : "dark",
              callback: (token: string) => {
                modalTurnstileRef.current = token;
              },
              "error-callback": () => {
                modalTurnstileRef.current = null;
              },
            }
          );
        }
      }, 100);

      return () => clearTimeout(timer);
    }

    // Cleanup the widget when the modal closes
    return () => {
      if (
        !IS_DEVELOPMENT &&
        modalTurnstileWidgetId.current &&
        window.turnstile
      ) {
        window.turnstile.remove(modalTurnstileWidgetId.current);
        modalTurnstileWidgetId.current = null;
      }
    };
  }, [showResendModal, theme]);

  // Reset Turnstile when coming back to the phone entry step
  useEffect(() => {
    if (
      !IS_DEVELOPMENT &&
      turnstileWidgetId.current &&
      window.turnstile &&
      step === LoginStep.PHONE_ENTRY
    ) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
      } catch (error) {
        console.warn("Failed to reset Turnstile widget:", error);
        // Re-initialize the widget if reset fails
        setTimeout(() => {
          initializeTurnstile();
        }, 100);
      }
    }
  }, [step]);

  // Validate phone on blur
  const validatePhoneOnBlur = () => {
    setPhoneValidationMsg(null);

    if (!phone.trim()) {
      setPhoneValidationMsg("Phone number is required");
      return;
    }

    if (!validateIndianPhone(phone)) {
      setPhoneValidationMsg("Please enter a valid Indian phone number");
    }
  };

  // Prepare for resend OTP by showing the modal
  const prepareResendOTP = () => {
    // Only proceed if the timer is not running
    if (timeLeft > 0) return;

    // Show the modal
    setShowResendModal(true);
    setError(null);
  };

  // Handle phone form submission
  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!phone.trim() || !validateIndianPhone(phone)) {
      setError("Please enter a valid Indian phone number");
      return;
    }

    // Skip Turnstile verification in development mode
    const turnstileToken = IS_DEVELOPMENT
      ? "dev_verification_token"
      : turnstileRef.current;

    if (!turnstileToken) {
      setError("Please complete the security check");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Format phone number to E.164 format
      const formattedPhone = formatIndianPhone(phone);

      // Request OTP for login
      const result = await requestLoginOTP(formattedPhone, turnstileToken);

      if (result.success) {
        setSuccess("OTP sent successfully to your phone");
        setTimeLeft(60); // Set 1 minute countdown for resend
        setStep(LoginStep.OTP_VERIFICATION);
      } else {
        setError(result.error || "Failed to send OTP. Please try again.");
        // Reset the Turnstile widget
        if (!IS_DEVELOPMENT && turnstileWidgetId.current && window.turnstile) {
          window.turnstile.reset(turnstileWidgetId.current);
        }
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error(err);
      // Reset the Turnstile widget
      if (!IS_DEVELOPMENT && turnstileWidgetId.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP verification
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otpCode.trim()) {
      setError("Please enter the OTP sent to your phone");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formattedPhone = formatIndianPhone(phone);
      const result = await verifyLoginOTP(formattedPhone, otpCode);

      if (result.success) {
        // Add success animation while keeping the loading state true
        setSuccess("Login successful!");

        // Short delay for success message animation, then navigate
        setTimeout(() => {
          // Direct redirect without delays - directly invoke the callback
          onLoginSuccess();
        }, 600); // Shorter delay for a more responsive feel
      } else {
        setTryCount((count) => count + 1);
        if (tryCount >= 2) {
          setError(
            "Maximum verification attempts reached. Please request a new OTP."
          );
        } else {
          setError(result.error || "Invalid OTP. Please try again.");
        }
        setLoading(false);
      }
    } catch (err: any) {
      // Check for 404 or other errors suggesting account doesn't exist
      const errorMessage =
        err.response?.status === 404
          ? "Phone number not registered. Please create an account first."
          : "An unexpected error occurred. Please try again.";
      setError(errorMessage);
      console.error(err);
      setLoading(false);
    }
  };

  // Handle OTP resend - use the modalTurnstileRef instead of turnstileRef
  const handleResendOTP = async () => {
    // Only proceed if the timer is not running
    if (timeLeft > 0) return;

    // Skip Turnstile verification in development mode
    const turnstileToken = IS_DEVELOPMENT
      ? "dev_verification_token"
      : modalTurnstileRef.current;

    if (!IS_DEVELOPMENT && !turnstileToken) {
      setError("Please complete the security check");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formattedPhone = formatIndianPhone(phone);
      const result = await resendOTP(formattedPhone, turnstileToken);

      if (result.success) {
        setSuccess("OTP has been resent to your phone");
        setTimeLeft(60); // Reset countdown
        setShowResendModal(false); // Hide the modal after successful resend

        // Reset the modal turnstile reference
        modalTurnstileRef.current = null;
      } else {
        setError(result.error || "Failed to resend OTP. Please try again.");
        // Reset the Turnstile widget if not in development mode
        if (
          !IS_DEVELOPMENT &&
          modalTurnstileWidgetId.current &&
          window.turnstile
        ) {
          window.turnstile.reset(modalTurnstileWidgetId.current);
        }
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error(err);
      // Reset the Turnstile widget if not in development mode
      if (
        !IS_DEVELOPMENT &&
        modalTurnstileWidgetId.current &&
        window.turnstile
      ) {
        window.turnstile.reset(modalTurnstileWidgetId.current);
      }
    } finally {
      setLoading(false);
    }
  };

  // Add a safe navigation function to handle going back to the phone entry step
  const safeNavigateToPhoneEntry = () => {
    // First set the step to avoid any race conditions
    setStep(LoginStep.PHONE_ENTRY);

    // If we're in production, try to reinitialize Turnstile after a short delay
    if (!IS_DEVELOPMENT && window.turnstile) {
      setTimeout(() => {
        try {
          initializeTurnstile();
        } catch (error) {
          console.warn("Failed to initialize Turnstile widget:", error);
        }
      }, 300);
    }
  };

  // Render OTP verification step with resend button but no inline resend UI
  const renderOtpVerificationStep = () => {
    return (
      <motion.form
        onSubmit={handleVerifyOTP}
        className="space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <label
            htmlFor="otp"
            className="block text-sm font-medium text-[color:var(--foreground)]"
          >
            Verification Code
          </label>
          <input
            type="text"
            id="otp"
            value={otpCode}
            onChange={(e) => {
              // Filter out non-numeric characters
              const numericValue = e.target.value.replace(/\D/g, "");
              setOtpCode(numericValue);
            }}
            placeholder="Enter the verification code"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            disabled={loading}
            className="mt-1 block w-full px-3 py-2 bg-transparent border border-[color:var(--border)] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] [&:-webkit-autofill]:text-[color:var(--foreground)] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:!transition-colors"
          />
        </motion.div>

        {error && (
          <motion.div
            className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {error}
          </motion.div>
        )}

        {success && (
          <motion.div
            className="p-3 bg-green-50 border border-green-200 rounded-md text-sm"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0, scale: [1, 1.03, 1] }}
            transition={{
              duration: 0.4,
              scale: { duration: 0.5, times: [0, 0.5, 1] },
            }}
          >
            <div className="flex items-center">
              <div className="shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                <motion.svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-green-600"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                >
                  <path d="M20 6L9 17l-5-5"></path>
                </motion.svg>
              </div>
              <span className="text-green-600 font-medium">{success}</span>
            </div>
          </motion.div>
        )}

        <motion.button
          type="submit"
          disabled={loading}
          className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          {loading ? "Verifying..." : "Login"}
        </motion.button>

        <motion.div
          className="mt-4 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <p className="text-sm font-semibold text-[color:var(--muted-foreground)]">
            Didn't receive the code?{" "}
            {timeLeft > 0 ? (
              <span>Resend in {timeLeft}s</span>
            ) : (
              <motion.button
                type="button"
                onClick={prepareResendOTP}
                disabled={loading}
                className="text-[color:var(--color-secondary)] hover:underline font-medium"
                whileHover={{ scale: 1.05 }}
              >
                Resend Code
              </motion.button>
            )}
          </p>
        </motion.div>

        {/* Change phone number link */}
        <motion.div
          className="!mt-2 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.9 }}
        >
          <button
            onClick={safeNavigateToPhoneEntry}
            className="text-sm text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] font-medium"
          >
            Change phone number?
          </button>
        </motion.div>
      </motion.form>
    );
  };

  // Render appropriate step UI
  const renderStep = () => {
    switch (step) {
      case LoginStep.PHONE_ENTRY:
        return (
          <motion.form
            onSubmit={handlePhoneSubmit}
            className="space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-[color:var(--foreground)]"
              >
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => {
                  // Filter out non-numeric characters
                  const numericValue = e.target.value.replace(/\D/g, "");
                  setPhone(numericValue);
                }}
                onBlur={validatePhoneOnBlur}
                placeholder="Enter your phone number"
                autoComplete="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                disabled={loading}
                className="mt-1 block w-full px-3 py-2 bg-transparent border border-[color:var(--border)] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--color-secondary)] focus:border-transparent text-[color:var(--foreground)] [&:-webkit-autofill]:text-[color:var(--foreground)] [&:-webkit-autofill]:bg-transparent [&:-webkit-autofill]:!transition-colors"
              />
              {phoneValidationMsg && (
                <motion.p
                  className="mt-1 text-sm text-red-600"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  {phoneValidationMsg}
                </motion.p>
              )}
            </motion.div>

            {/* Turnstile Widget - Only show in production */}
            {!IS_DEVELOPMENT && (
              <motion.div
                className="flex justify-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
              >
                <div ref={turnstileContainer}></div>
              </motion.div>
            )}

            {error && (
              <motion.div
                className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <p>{error}</p>
                {/* Show signup link if error suggests account doesn't exist */}
                {error.includes("not registered") && (
                  <p className="mt-2 font-medium">
                    <Link
                      to="/signup"
                      className="text-[color:var(--color-secondary)] hover:underline"
                    >
                      Don't have an account? Sign up here
                    </Link>
                  </p>
                )}
              </motion.div>
            )}

            {success && (
              <motion.div
                className="p-3 bg-green-50 border border-green-200 rounded-md text-sm"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0, scale: [1, 1.03, 1] }}
                transition={{
                  duration: 0.4,
                  scale: { duration: 0.5, times: [0, 0.5, 1] },
                }}
              >
                <div className="flex items-center">
                  <div className="shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                    <motion.svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-green-600"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.4, delay: 0.2 }}
                    >
                      <path d="M20 6L9 17l-5-5"></path>
                    </motion.svg>
                  </div>
                  <span className="text-green-600 font-medium">{success}</span>
                </div>
              </motion.div>
            )}

            {IS_DEVELOPMENT && (
              <motion.div
                className="p-3 bg-blue-50 border border-blue-200 text-blue-600 rounded-md text-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                Development mode: Turnstile verification bypassed
              </motion.div>
            )}

            <motion.button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              {loading ? "Sending..." : "Send OTP"}
            </motion.button>
          </motion.form>
        );

      case LoginStep.OTP_VERIFICATION:
        return renderOtpVerificationStep();

      default:
        return null;
    }
  };

  return (
    <>
      <AuthNavbar />
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
        <div className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8">
          {/* Back to Login button at the top right - only show in OTP verification step */}
          {step === LoginStep.OTP_VERIFICATION && (
            <div className="flex justify-start mb-4">
              <button
                onClick={safeNavigateToPhoneEntry}
                className="flex items-center text-sm text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] transition-colors px-3 py-1.5 rounded-full border border-[color:var(--border)] bg-[color:var(--card)] hover:bg-[color:var(--muted)]/10"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-1"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                Back to Login
              </button>
            </div>
          )}

          <div className="flex flex-col items-center mb-8">
            <motion.h2
              className="text-3xl pt-2 font-bold text-[color:var(--foreground)]"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Voice Agent Portal
            </motion.h2>
            <motion.p
              className="text-sm text-[color:var(--muted-foreground)] mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {step === LoginStep.PHONE_ENTRY
                ? "Enter your phone number to receive a login code"
                : "Enter the verification code sent to your phone"}
            </motion.p>
          </div>

          {renderStep()}

          <motion.div
            className="mt-6 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Link
              to="/admin/login"
              className="text-sm text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] font-medium"
            >
              Admin? Login here
            </Link>
            <div className="mt-2">
              <p className="text-sm text-[color:var(--foreground)]">
                <Link
                  to="/signup"
                  className="text-[color:var(--foreground)] hover:text-[color:var(--color-secondary)] font-medium"
                >
                  Don't have an account? Sign up here
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Resend OTP Modal */}
      <AnimatePresence>
        {showResendModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowResendModal(false)}
          >
            <motion.div
              className="bg-[color:var(--card)] rounded-lg shadow-xl max-w-md w-full p-6"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-[color:var(--foreground)]">
                  Resend Verification Code
                </h3>
                <button
                  onClick={() => setShowResendModal(false)}
                  className="text-[color:var(--muted-foreground)] hover:text-[color:var(--foreground)] transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <p className="text-sm text-[color:var(--muted-foreground)] mb-4">
                Please complete the security check to receive a new verification
                code.
              </p>

              {/* Turnstile Widget - using modal-specific container */}
              {!IS_DEVELOPMENT ? (
                <div className="flex justify-center mb-4">
                  <div ref={modalTurnstileContainer}></div>
                </div>
              ) : (
                <div className="p-3 bg-blue-50 border border-blue-200 text-blue-600 rounded-md text-sm mb-4">
                  Development mode: Turnstile verification bypassed
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm mb-4">
                  {error}
                </div>
              )}

              <div className="flex space-x-3">
                <motion.button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={
                    loading || (!IS_DEVELOPMENT && !modalTurnstileRef.current)
                  }
                  className="flex-1 py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? "Sending..." : "Resend Code"}
                </motion.button>

                <motion.button
                  type="button"
                  onClick={() => setShowResendModal(false)}
                  className="py-2 px-4 border border-[color:var(--border)] text-[color:var(--foreground)] rounded-md text-sm hover:bg-[color:var(--muted)]/10 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10">
          <LoadingState
            size={
              step === LoginStep.OTP_VERIFICATION && success
                ? "large"
                : "medium"
            }
            padding="none"
            message={
              step === LoginStep.OTP_VERIFICATION && success
                ? "Redirecting..."
                : step === LoginStep.PHONE_ENTRY
                ? "Sending OTP..."
                : "Verifying..."
            }
            fullPage={success ? true : false}
          />
        </div>
      )}
    </>
  );
};

export default LeadLogin;
